import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Text, Alert, Button, Select } from '@mantine/core';
import { LoadingState } from '../components/common/LoadingState';
import { SchoolMap } from '../components/map';
import { geocodingService, GeocodeResult } from '../services/GeocodingService';
import { getSavedTileStyle } from '../utils/mapTileUtils';
import type { FullSchool } from '../types/Localities';

export type EntityMapTabMode = 'locality' | 'addressFields';

export interface AddressFieldConfig {
    fieldName: string;
    label: string;
}

export interface UseEntityMapTabConfig {
    mode: EntityMapTabMode;
    /** For addressFields mode: which entity fields contain address strings. */
    addressFields?: AddressFieldConfig[];
    tabLabel?: string;
    /** Return true if this entity type supports a map tab. */
    supportsMapping: (entityType: string) => boolean;
    /** For locality mode: return true if entity has mappable location (Address/City/State/Zip or Lat/Long). */
    hasLocationData?: (entity: any) => boolean;
}

export interface UseEntityMapTabResult {
    additionalTabs: Array<{ label: string; value: number; content: React.ReactNode }>;
    handleTabChange: (tabIndex: number) => void;
    /** Index of the map tab when present (for parent to sync activeTab). */
    mapTabIndex: number | null;
    additionalTabsStartIndex: number;
}

/**
 * Collect valid address strings from entity using configured field names.
 * Keeps one entry per field (so Drop-off and Pickup both show in the selector even when the address text is the same).
 */
function collectAddressesFromEntity(
    entity: any,
    addressFields: AddressFieldConfig[]
): Array<{ id: string; label: string; address: string }> {
    const out: Array<{ id: string; label: string; address: string }> = [];
    for (const { fieldName, label } of addressFields) {
        const raw = entity?.[fieldName] ?? entity?.[fieldName.toLowerCase?.()];
        const value = typeof raw === 'string' ? raw.trim() : '';
        if (!value) continue;
        out.push({ id: `${fieldName}:${value}`, label, address: value });
    }
    return out;
}

/**
 * Build a minimal FullSchool-like object for the map from a geocode result and label/address.
 */
function toMapMarker(
    geocodeResult: GeocodeResult,
    label: string,
    addressDisplay: string
): FullSchool {
    return {
        Id: 'map-pin',
        DisplayName: label,
        EntityType: 'Map',
        Address: addressDisplay || `${geocodeResult.lat.toFixed(6)}, ${geocodeResult.lng.toFixed(6)}`,
        City: '-',
        State: '-',
        Zip: '',
        Latitude: geocodeResult.lat,
        Longitude: geocodeResult.lng
    };
}

export interface UseEntityMapTabParams {
    entity: any | null;
    entityType: string;
    config: UseEntityMapTabConfig;
    /** Same as EntityDetailDialog: start index for additional tabs (e.g. 2 or 3). */
    additionalTabsStartIndex: number;
}

/**
 * Reusable hook for adding a Map tab to entity detail dialogs.
 * Supports (1) locality mode: single address from entity Address/City/State/Zip or Lat/Long,
 * and (2) addressFields mode: multiple address strings from named fields (e.g. DropOffAddress, PickupAddress)
 * with a selector to choose which to display on the map.
 */
export function useEntityMapTab({
    entity,
    entityType,
    config,
    additionalTabsStartIndex
}: UseEntityMapTabParams): UseEntityMapTabResult {
    const {
        mode,
        addressFields = [],
        tabLabel = 'Map',
        supportsMapping,
        hasLocationData
    } = config;

    const [geocodeResult, setGeocodeResult] = useState<GeocodeResult | null>(null);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [geocodingError, setGeocodingError] = useState<string | null>(null);
    const [mapTabRequested, setMapTabRequested] = useState(false);
    const [hasAttemptedGeocoding, setHasAttemptedGeocoding] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
    const [mapTabIndex, setMapTabIndex] = useState<number | null>(null);
    /** For addressFields mode: which address is selected (id). */
    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);

    const handleTabChange = useCallback((tabIndex: number) => {
        setActiveTabIndex(tabIndex);
        const isMapTab = mapTabIndex !== null && tabIndex === mapTabIndex;
        if (isMapTab) {
            if (geocodingError) {
                setGeocodingError(null);
                setHasAttemptedGeocoding(false);
            }
            setMapTabRequested(true);
        } else {
            setHasAttemptedGeocoding(false);
        }
    }, [mapTabIndex, geocodingError]);

    const addresses = useMemo(() => {
        if (mode !== 'addressFields' || !entity || !addressFields.length) return [];
        return collectAddressesFromEntity(entity, addressFields);
    }, [mode, entity, addressFields]);

    const selectedAddress = useMemo(() => {
        if (!addresses.length) return null;
        if (selectedAddressId) {
            const found = addresses.find(a => a.id === selectedAddressId);
            if (found) return found;
        }
        return addresses[0];
    }, [addresses, selectedAddressId]);

    useEffect(() => {
        setMapTabRequested(false);
        setActiveTabIndex(null);
        setMapTabIndex(null);
        setGeocodeResult(null);
        setIsGeocoding(false);
        setGeocodingError(null);
        setHasAttemptedGeocoding(false);
        setSelectedAddressId(null);
    }, [entity, entityType]);

    const shouldShowMapTab = useMemo(() => {
        if (!entity) return false;
        if (!supportsMapping(entityType)) return false;
        if (mode === 'locality') return hasLocationData?.(entity) ?? false;
        return addresses.length > 0;
    }, [entity, entityType, mode, supportsMapping, hasLocationData, addresses.length]);

    const addressToGeocode = mode === 'locality'
        ? entity
        : selectedAddress?.address ?? null;

    useEffect(() => {
        if (!shouldShowMapTab || mapTabIndex === null || activeTabIndex !== mapTabIndex || !mapTabRequested) return;
        if (geocodeResult && mode === 'locality') return;
        if (mode === 'addressFields' && (!selectedAddress || (geocodeResult && addressToGeocode === selectedAddress.address))) return;
        if (isGeocoding || hasAttemptedGeocoding) return;

        const runGeocode = async () => {
            setHasAttemptedGeocoding(true);
            setIsGeocoding(true);
            setGeocodingError(null);
            try {
                let result: GeocodeResult | null = null;
                if (mode === 'locality') {
                    result = await geocodingService.getCoordinates(entity, entityType);
                } else if (typeof addressToGeocode === 'string' && addressToGeocode.trim()) {
                    result = await geocodingService.getCoordinatesFromAddressString(addressToGeocode);
                }
                setGeocodeResult(result);
            } catch (err) {
                setGeocodingError(err instanceof Error ? err.message : 'Geocoding failed');
            } finally {
                setIsGeocoding(false);
            }
        };

        runGeocode();
    }, [activeTabIndex, mapTabIndex, mapTabRequested, entity, entityType, mode, addressToGeocode, selectedAddress?.address, geocodeResult, isGeocoding, hasAttemptedGeocoding, shouldShowMapTab]);

    const tileStyle = getSavedTileStyle();

    const renderMapContent = useCallback(() => {
        if (isGeocoding) {
            return (
                <LoadingState variant="centered" label="Getting coordinates..." size={24} />
            );
        }

        if (geocodingError) {
            const addressLine = mode === 'locality' && entity
                ? `${entity.Address ?? ''}, ${entity.City ?? ''}, ${entity.State ?? ''} ${entity.Zip ?? ''}`.replace(/^,\s*|,\s*$/g, '').trim()
                : addressToGeocode ?? '';
            return (
                <Box p="md">
                    <Alert
                        color="red"
                        mb="md"
                        title="Geocoding Error"
                    >
                        <Text size="sm">{geocodingError}</Text>
                        <Button
                            variant="subtle"
                            size="xs"
                            mt="xs"
                            onClick={() => {
                                setGeocodeResult({ lat: 30.3322, lng: -81.6557, source: 'fallback' });
                                setGeocodingError(null);
                            }}
                        >
                            Use Fallback Location
                        </Button>
                    </Alert>
                    {addressLine && (
                        <Text size="sm" c="dimmed" mt="md">
                            <strong>Address:</strong> {addressLine}
                        </Text>
                    )}
                </Box>
            );
        }

        if (geocodeResult) {
            const sourceInfo = geocodeResult.source === 'static' ? 'pre-computed' :
                geocodeResult.source === 'nominatim' ? 'live geocoded' : 'approximate location';
            const label = mode === 'addressFields' && selectedAddress ? selectedAddress.label : entityType;
            const addressDisplay = mode === 'addressFields' && selectedAddress ? selectedAddress.address : (entity?.Address && `${entity.Address}, ${entity.City ?? ''}, ${entity.State ?? ''} ${entity.Zip ?? ''}`.trim());
            const marker = toMapMarker(geocodeResult, label, addressDisplay ?? '');
            const schools: FullSchool[] = [marker];

            return (
                <Box>
                    {mode === 'addressFields' && addresses.length > 0 && (
                        <Select
                            size="sm"
                            label="Address"
                            placeholder="Select address"
                            value={selectedAddress?.id ?? null}
                            data={addresses.map((a) => ({ value: a.id, label: `${a.label}: ${a.address}` }))}
                            onChange={(value) => {
                                setSelectedAddressId(value ?? null);
                                setGeocodeResult(null);
                                setHasAttemptedGeocoding(false);
                            }}
                            miw={200}
                            mb="md"
                        />
                    )}
                    <Text fw={600} size="lg" mb="xs">
                        {mode === 'addressFields' && selectedAddress ? `${selectedAddress.label} location` : 'Location'}
                    </Text>
                    {addressDisplay && (
                        <Text size="sm" c="dimmed" mb="xs">
                            {addressDisplay}
                        </Text>
                    )}
                    <Text size="xs" c="dimmed" display="block" mb="xs">
                        Coordinates: {geocodeResult.lat.toFixed(6)}, {geocodeResult.lng.toFixed(6)} ({sourceInfo})
                    </Text>
                    <SchoolMap
                        schools={schools}
                        selectedSchool={marker}
                        height="400px"
                        showPopup={false}
                        tileStyle={tileStyle}
                    />
                    {geocodeResult.display_name && (
                        <Text size="sm" c="dimmed" mt="md">
                            <strong>Geocoded:</strong> {geocodeResult.display_name}
                        </Text>
                    )}
                </Box>
            );
        }

        return (
            <Box p="md" ta="center">
                <Text size="sm" c="dimmed">
                    Unable to get coordinates.
                </Text>
            </Box>
        );
    }, [mode, entity, entityType, addresses, selectedAddress, addressToGeocode, geocodeResult, isGeocoding, geocodingError, tileStyle]);

    const additionalTabs = useMemo(() => {
        if (!shouldShowMapTab) return [];
        return [{ label: tabLabel, value: additionalTabsStartIndex, content: renderMapContent() }];
    }, [shouldShowMapTab, tabLabel, additionalTabsStartIndex, renderMapContent]);

    const calculatedMapTabIndex = additionalTabs.length > 0 ? additionalTabsStartIndex : null;

    useEffect(() => {
        if (calculatedMapTabIndex !== null && mapTabIndex !== calculatedMapTabIndex) {
            setMapTabIndex(calculatedMapTabIndex);
        }
    }, [calculatedMapTabIndex, mapTabIndex]);

    return {
        additionalTabs,
        handleTabChange,
        mapTabIndex: calculatedMapTabIndex,
        additionalTabsStartIndex
    };
}
